import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { LossType, Management } from '../types/pregnancy';

interface Props {
  type: LossType;
  rhesusStatus: 'positive' | 'negative' | null;
  onSave: (data: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
  /** If set, the form renders in read-only/edit mode with existing data. */
  existing?: Record<string, unknown> | null;
}

const SURGICAL_MISCARRIAGE_PROCEDURES = ['D&C', 'MVA', 'EVA', 'Salpingectomy', 'Salpingostomy', 'Other'];
const SURGICAL_TERMINATION_PROCEDURES = ['MVA', 'EVA', 'D&C', 'D&E', 'Other'];
const MEDICAL_MISCARRIAGE_PROCEDURES = ['Methotrexate', 'Misoprostol only', 'Other'];
const MEDICAL_TERMINATION_PROCEDURES = ['Mifepristone + Misoprostol', 'Misoprostol only', 'Other'];
const MISCARRIAGE_TYPES = ['Complete', 'Incomplete', 'Missed', 'Threatened (resolved)', 'Ectopic', 'Molar', 'Other'];
const MISCARRIAGE_TYPE_VALUES: Record<string, string> = {
  'Complete': 'complete', 'Incomplete': 'incomplete', 'Missed': 'missed',
  'Threatened (resolved)': 'threatened_resolved', 'Ectopic': 'ectopic', 'Molar': 'molar', 'Other': 'other',
};

export function LossForm({ type, rhesusStatus, onSave, onCancel, existing }: Props) {
  const init = existing || {};
  const [date, setDate] = useState((init.date as string) || new Date().toISOString().slice(0, 10));
  const [gestWeeks, setGestWeeks] = useState<number>((init.gestationWeeks as number) || 0);
  const [gestDays, setGestDays] = useState<number>((init.gestationDays as number) || 0);
  const [management, setManagement] = useState<Management>((init.management as Management) || '');
  const [procedure, setProcedure] = useState<string>((init.procedure as string) || '');
  const [procedureDetails, setProcedureDetails] = useState((init.procedureDetails as string) || '');
  const [antiDGiven, setAntiDGiven] = useState<boolean | null>((init.antiDGiven as boolean | null) ?? null);
  const [complications, setComplications] = useState((init.complications as string) || '');
  const [notes, setNotes] = useState((init.notes as string) || '');
  const [miscarriageType, setMiscarriageType] = useState((init.miscarriageType as string) || '');
  const [histologySent, setHistologySent] = useState((init.histologySent as boolean) || false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const showAntiD = rhesusStatus === 'negative' || rhesusStatus === null;
  const isMiscarriage = type === 'miscarriage';

  const procedureOptions = management === 'surgical'
    ? (isMiscarriage ? SURGICAL_MISCARRIAGE_PROCEDURES : SURGICAL_TERMINATION_PROCEDURES)
    : management === 'medical'
      ? (isMiscarriage ? MEDICAL_MISCARRIAGE_PROCEDURES : MEDICAL_TERMINATION_PROCEDURES)
      : [];

  const handleSave = async () => {
    if (!date) { setError('Date is required'); return; }
    if (showAntiD && antiDGiven === null) { setError('Anti-D status must be recorded for Rh-negative or unknown patients'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({
        type,
        date,
        gestationWeeks: gestWeeks,
        gestationDays: gestDays,
        management,
        procedure: management === 'expectant' ? null : procedure || null,
        procedureDetails,
        antiDGiven,
        histologySent: isMiscarriage ? histologySent : false,
        miscarriageType: isMiscarriage ? miscarriageType || null : null,
        complications,
        notes,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
        {isMiscarriage ? 'Record Miscarriage' : 'Record Termination'}
      </h3>

      {error && <div className="mb-3 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="space-y-3">
        {/* Date + Gestation */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date *</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Gestation (weeks)</label>
            <input type="number" min={0} max={45} value={gestWeeks} onChange={e => setGestWeeks(parseInt(e.target.value) || 0)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Days</label>
            <input type="number" min={0} max={6} value={gestDays} onChange={e => setGestDays(parseInt(e.target.value) || 0)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
          </div>
        </div>

        {/* Miscarriage type (miscarriage only) */}
        {isMiscarriage && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Miscarriage Type</label>
            <select value={miscarriageType} onChange={e => setMiscarriageType(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white">
              <option value="">— Select —</option>
              {MISCARRIAGE_TYPES.map(t => <option key={t} value={MISCARRIAGE_TYPE_VALUES[t]}>{t}</option>)}
            </select>
          </div>
        )}

        {/* Management */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Management</label>
          <select value={management} onChange={e => { setManagement(e.target.value as Management); setProcedure(''); }} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white">
            <option value="">— Select —</option>
            <option value="expectant">Expectant</option>
            <option value="medical">Medical</option>
            <option value="surgical">Surgical</option>
          </select>
        </div>

        {/* Procedure (conditional) */}
        {management && management !== 'expectant' && procedureOptions.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Procedure</label>
            <select value={procedure} onChange={e => setProcedure(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white">
              <option value="">— Select —</option>
              {procedureOptions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        )}

        {/* Procedure details */}
        {procedure && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Procedure Details</label>
            <textarea value={procedureDetails} onChange={e => setProcedureDetails(e.target.value)} rows={2} placeholder="Dosage, specifics, additional notes" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
          </div>
        )}

        {/* Anti-D */}
        {showAntiD && (
          <div className={`p-2 rounded ${rhesusStatus === 'negative' ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-200'}`}>
            <label className="block text-xs font-semibold mb-1 ${rhesusStatus === 'negative' ? 'text-amber-800' : 'text-gray-700'}">
              Anti-D Given {rhesusStatus === 'negative' ? '(REQUIRED — Rh Negative patient)' : '(Rh status unknown)'}
            </label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1 text-sm"><input type="radio" checked={antiDGiven === true} onChange={() => setAntiDGiven(true)} /> Yes</label>
              <label className="flex items-center gap-1 text-sm"><input type="radio" checked={antiDGiven === false} onChange={() => setAntiDGiven(false)} /> No</label>
            </div>
          </div>
        )}

        {/* Histology (miscarriage only) */}
        {isMiscarriage && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={histologySent} onChange={e => setHistologySent(e.target.checked)} className="h-4 w-4 rounded" />
            Histology Sent
            {['missed', 'incomplete', 'molar'].includes(miscarriageType) && (
              <span className="text-xs text-amber-600 ml-1">(Recommended)</span>
            )}
          </label>
        )}

        {/* Complications + Notes */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Complications</label>
          <textarea value={complications} onChange={e => setComplications(e.target.value)} rows={2} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-4">
        <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
        <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
          {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</> : 'Save'}
        </button>
      </div>
    </div>
  );
}
